import {CollapsibleNavList} from '@github-ui/layout-primitives/CollapsibleNavList'
import {useCollapsibleSidebarContext} from '@github-ui/layout-primitives/CollapsibleSidebar'
import {SidebarTooltip} from '@github-ui/layout-primitives/SidebarTooltip'
import {Link} from '@github-ui/react-router'
import type {SidebarLink} from '@github-ui/saved-views'
import {useAnalytics} from '@github-ui/use-analytics'
import {BookmarkIcon, PlusIcon} from '@primer/octicons-react'
import {ActionList, NavList, Truncate} from '@primer/react'

import {SidebarLinkIcon} from './components/sidebar-link-icon/SidebarLinkIcon'
import {SidebarList, type SidebarItemWrapperProps} from './components/sidebar-list/SidebarList'
import {SidebarSavedViews} from './components/sidebar-saved-views/SidebarSavedViews'
import type {DashboardTypes} from './types/dashboard-types'
import {getHandlerNavItemClick} from './utils/sidebar-link'

import styles from './DashboardSidebarCollapsible.module.css'

/**
 * Wraps each nav item in a tooltip once the surrounding `CollapsibleSidebar`
 * condenses to the icon-only rail, where the text label is clipped and the
 * icon is the only remaining affordance. Renders the item untouched while
 * expanded, and when there is no collapsible sidebar ancestor at all — the
 * context defaults to expanded, so non-collapsible layouts are unaffected.
 */
function CondensedTooltipItem({linkItem, children}: SidebarItemWrapperProps) {
  const {visualIsExpanded} = useCollapsibleSidebarContext()
  if (visualIsExpanded) return children
  return <SidebarTooltip text={linkItem.name}>{children}</SidebarTooltip>
}

interface DashboardSidebarCollapsibleProps {
  dashboardType: DashboardTypes
  links: SidebarLink[]
  selectedLink: string
  onSelectLink: (linkItem: SidebarLink) => void
  savedViews: SidebarLink[]
  showSavedViews?: boolean
  onCreateView?: () => void
  generateLinkPath: (path: string | undefined, query?: string) => string
}

/**
 * Collapse-aware variant of `DashboardSidebar`, built for
 * `SidebarPageLayout`/`FilteredListPageLayout`.
 *
 * `DashboardSidebar` is width-agnostic and `DashboardSidebarCollapsed` is the
 * legacy narrow-viewport treatment (a dropdown button opening a modal), which
 * cannot render inside a 64px icon rail. This variant instead lets the layout
 * own the collapse and adapts its own contents to it:
 *
 * - Default links keep `DashboardSidebar`'s `SidebarList` rendering (icons are
 *   already `NavList.LeadingVisual`s, so the rail shows them) and gain a
 *   tooltip per item when condensed.
 */
export function DashboardSidebarCollapsible({
  dashboardType,
  links,
  selectedLink,
  onSelectLink,
  savedViews,
  showSavedViews = false,
  onCreateView,
  generateLinkPath,
}: DashboardSidebarCollapsibleProps) {
  const {sendAnalyticsEvent} = useAnalytics()
  const {isExpanded} = useCollapsibleSidebarContext()

  return (
    <>
      <SidebarList
        dashboardType={dashboardType}
        links={links}
        selectedLink={selectedLink}
        onSelectLink={onSelectLink}
        generateLinkPath={generateLinkPath}
        ItemWrapper={CondensedTooltipItem}
      />
      {showSavedViews &&
        onCreateView &&
        (isExpanded ? (
          <div className={styles.expandedSavedViews}>
            <SidebarSavedViews
              dashboardType={dashboardType}
              savedViews={savedViews}
              selectedLink={selectedLink}
              onSelectLink={onSelectLink}
              onCreateView={onCreateView}
              generateLinkPath={generateLinkPath}
            />
          </div>
        ) : (
          <>
            <hr className={styles.divider} />
            <CollapsibleNavList aria-label="Saved views">
              <CollapsibleNavList.Group
                title="Views"
                icon={BookmarkIcon}
                trailingAction={({close}) => (
                  <ActionList.GroupHeading.TrailingAction
                    as="button"
                    icon={PlusIcon}
                    label="Create view"
                    tooltipDirection="e"
                    onClick={() => {
                      close()
                      onCreateView()
                    }}
                  />
                )}
              >
                {savedViews.map(linkItem => (
                  // `CollapsibleNavList.Item` types its props as an `<a>`, so it rejects the router's
                  // `to` without a cast. Its only extra behavior is hiding rows without a
                  // `LeadingVisual` when condensed doesn't apply here since these rows only render
                  // inside the popover.
                  <NavList.Item
                    as={Link}
                    key={linkItem.id}
                    to={generateLinkPath(linkItem.id, linkItem.query)}
                    aria-current={selectedLink === linkItem.id && 'page'}
                    onClick={getHandlerNavItemClick({dashboardType, onSelectLink, sendAnalyticsEvent, linkItem})}
                  >
                    <NavList.LeadingVisual>
                      <SidebarLinkIcon name={linkItem.icon} color={linkItem.color} />
                    </NavList.LeadingVisual>
                    <Truncate title={linkItem.name} maxWidth="100%">
                      {linkItem.name}
                    </Truncate>
                  </NavList.Item>
                ))}
              </CollapsibleNavList.Group>
            </CollapsibleNavList>
          </>
        ))}
    </>
  )
}
